'use client'

// import "swiper/swiper-bundle.css";
import React, { useEffect, useRef } from "react";
import Headline from "@/components/ui/Headline/Headline";
import { LanguageType, LanguageDirection, LPServiceSectionContent } from "@/lib/types";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Mousewheel } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper/types";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import '@/components/ui/TestimonialCard/TestimonialCard.css';
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import Subheading from "@/components/ui/Subheading/Subheading";
import ServiceShowcaseCards from "@/components/client/ServiceShowcaseCards/ServiceShowcaseCards";
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface DevicesSectionProps {
  language?: LanguageType
  headlineText: string
  subheadingText: string
  servicesSectionPageData: LPServiceSectionContent
  onInitialPosition?: (position: number) => void
  buttonText?: string
  testimonial?: {
    quote: string
    author: string
  }
  onOpenSlidingForm?: () => void
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

interface ImageCard {
  imageUrl: string;
  altText: string;
  title: string;
  description: string;
  logoUrl: string;
}


const LPServicesSection: React.FC<DevicesSectionProps> = ({
  language = "english",
  servicesSectionPageData,
  headlineText,
  subheadingText,
  onInitialPosition,
  buttonText = "Start your treatment plan",
  testimonial,
  onOpenSlidingForm,
  id,
}) => {
  const isLg = useIsLg();
  const isTb = useIsTb();
  const swiperRef = useRef<SwiperType>(null);
  const sectionRef = useRef<HTMLElement>(null);
  // const containerRef = useRef<HTMLDivElement>(null);

  const imageCards: ImageCard[] = servicesSectionPageData.services.map((service) => ({
    imageUrl: service.serviceImage.url,
    altText: service.serviceImageAltText,
    title: service.serviceName,
    description: service.serviceDescription,
    logoUrl: service.serviceLogo.url
  }));


  useEffect(() => {
    if (sectionRef.current && onInitialPosition) {
      const rect = sectionRef.current.getBoundingClientRect();
      const bottomY = window.scrollY + rect.bottom;
      onInitialPosition(bottomY);
    }
  }, [isLg, onInitialPosition]);

  // TODO: triggers are a bit off here - dont know why atm

  useGSAP(() => {
    // const container = containerRef.current;
    // if (container) {
    if (isLg) {
      const cards: HTMLElement[] = Array.from(document.querySelectorAll('.device-image-card'));

      cards.forEach(card => {
        gsap.fromTo(card, {
          yPercent: -10,
        }, {
          yPercent: 10,
          ease: "none",
          force3D: true,
          scrollTrigger: {
            trigger: card,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,
            // markers: true,
            id: "devices-section-scroll-trigger"
          },
          onEnter: () => {
            card.style.willChange = "transform";
          },
          onLeave: () => {
            card.style.willChange = "auto";
          },
          onEnterBack: () => {
            card.style.willChange = "transform";
          },
          onLeaveBack: () => {
            card.style.willChange = "auto";
          }
        });
      });
    }
    // }, [] );
  }, { dependencies: [isLg], revertOnUpdate: true });
  // }, { dependencies: [], revertOnUpdate: true});

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];
    }
    return () => {
      if (swiperRef.current) {
      }
    };
  }, []);

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      id={id}
      style={{ overscrollBehaviorX: 'none' }}
      // overflow-hidden
      className="
      w-full flex flex-col items-center justify-center
      gap-[2.5rem] lg:gap-[6rem]
      padding-main-x-mobile
      padding-main-x-desktop
    ">
      <div
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="
          w-full container-x-large"
      >

        <div className="flex flex-row items-center justify-center w-full">
          {/* Figma Heading Component, Center, Size L at its full 997px width */}
          <Headline
            type="wide"
            alignment='center'
            heading={headlineText}
            // paragraph={paragraphText}
            subheading={subheadingText}
            language={language}
            shouldAnimate={false}
          />

        </div>
      </div>

      <div
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="w-full container-x-large overflow-visible">

        {/* Desktop Grid Layout */}
        {isLg && (
          <div
            // ref={containerRef}
            className="
              gap-y-[2.5rem] gap-x-[1rem]
              grid
              [grid-template-columns:repeat(auto-fit,minmax(min(100%,27rem),1fr))]
              4xl:[grid-template-columns:repeat(auto-fit,minmax(min(100%,34rem),1fr))]
            ">
            {imageCards.map((card) => (
              <ServiceShowcaseCards
                key={card.title}
                imageUrl={card.imageUrl}
                imageAlt={card.title}
                title={card.title}
                description={card.description}
                language={language}
                imageWidth={960}
                imageHeight={540}
                imageSizes="(min-width: 992px) 33vw, 100vw"
                imageAspectRatio="16 / 9"
                wrapperGapClass="lg:gap-[1.5rem]"
                textGapClass="lg:gap-[1.25rem]"
                paddingMode="directional"
                paragraphMaxWidth="43rem"
              />
            ))}
          </div>
        )}

        {/* Mobile Swiper Layout */}
        {!isLg && (
          <div className="w-full overflow-hidden">
            <Swiper
              style={{
                width: '100%',
                overflow: 'visible'
              }}
              modules={[Pagination, Mousewheel]}
              pagination={{
                clickable: true,
                el: '.swiper-custom-pagination',
                bulletClass: 'swiper-pagination-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active',
              }}
              loop={imageCards.length > 1}
              spaceBetween={isTb ? 16 : 12}
              slidesPerView="auto"
              onBeforeInit={(swiper) => {
                swiperRef.current = swiper;
              }}
              centeredSlidesBounds={isTb ? false : true}
              centeredSlides={isTb ? false : true}
              observer={true}
              observeSlideChildren={true}
              resizeObserver={true}
              // mousewheel={{
              //   forceToAxis: true,
              //   sensitivity: 0.5,
              //   releaseOnEdges: true,
              // }}
              className="!overflow-visible testimonial-swiper pb-8"
            >
              <div className="w-full overflow-hidden">
                <div className="w-full">
                  <div className="flex w-full">
                    {imageCards.map((card) => (
                      <SwiperSlide
                        key={card.title}
                        style={{
                          width: '100%',
                        }}
                        className="cursor-grab"
                      >
                        <ServiceShowcaseCards
                          imageUrl={card.imageUrl}
                          imageAlt={card.title}
                          title={card.title}
                          description={card.description}
                          language={language}
                          imageWidth={900}
                          imageHeight={600}
                          imageSizes="100vw"
                          imageAspectRatio="3 / 2"
                          wrapperGapClass="tb:gap-[1.5rem]"
                          paddingMode="symmetric"
                        />
                      </SwiperSlide>
                    ))}
                  </div>
                </div>
              </div>

              {imageCards.length > 1 && (
                <div
                  style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                  className="mt-[1.5rem] lg:mt-[2.5rem] mb-[3px] px-[3px] w-full flex items-center justify-between"
                >
                  <CompactButton size={44} radius={9999} onClick={handlePrev}>
                    {language === "english" ? <CaretLeft /> : <CaretRight />}
                  </CompactButton>
                  <div className="swiper-custom-pagination flex justify-center gap-[0.625rem]" />
                  <CompactButton size={44} radius={9999} onClick={handleNext}>
                    {language === "english" ? <CaretRight /> : <CaretLeft />}
                  </CompactButton>
                </div>
              )}
            </Swiper>
          </div>
        )}

        {/* CTA + testimonial - 72px below the cards on desktop, 40px on
            mobile (8px margin, which keeps the CTA glow from being clipped,
            + 32px padding) */}
        <div className="
          w-full mt-[0.5rem] pt-[2rem] lg:mt-[4.5rem] lg:pt-0
          flex flex-col items-center justify-center gap-[1.5rem] lg:gap-[2rem]
        ">
          <div
            style={{ direction: "ltr" }}
            className="w-fit">
            <PrimaryButton
              text={buttonText}
              onClick={() => {
                if (onOpenSlidingForm) {
                  onOpenSlidingForm();
                }
              }}
              size={isLg ? 64 : 52}
              type="accent"
              language={language}
              icon={true}
              isLPCtaButton={true}
            />
          </div>
          {testimonial && (
            <div
              style={{ direction: "ltr" }}
              className="w-full text-center flex flex-col items-center justify-center gap-[1rem] lg:gap-[1.25rem]">
              <p dir="auto" className="text-text-quaternary text-20--alexandria">
                {testimonial.quote}
              </p>
              <Subheading
                size={isLg ? 20 : 16}
                subheading={testimonial.author}
                alignment="center"
                textColor="text-text-quaternary"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


export default LPServicesSection;
